import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  CROSSFADE_MS,
  EXPAND_EASING,
  EXPAND_FLIGHT_MS,
  type FlightGeometry,
  type TransitionRect,
  computeFlightGeometry,
  fireLightHaptic,
  flightFrameAt,
  frameToTransform,
  isDegenerateRect,
  readFontSizePx,
  rectOf,
} from './sharedElementTransition'
import { prefersReducedMotion } from './scoreCountUp'

/** How the detail route should render itself for the current transition:
 *  - `idle` — no transition (deep link / opened from elsewhere): render normally.
 *  - `entering` — clone is flying in: real name hidden, header/list content hidden pre-stagger.
 *  - `settled` — flight done: real name shown, content staggers in.
 *  - `leaving` — reversing an interrupted expand flight back to the source.
 *  - `crossfade-in` — reduced-motion / skipped shared element: fade the whole route in.
 *  - `crossfade-out` — reduced-motion / skipped collapse: fade the whole route out. */
export type DetailEntryState =
  | 'idle'
  | 'entering'
  | 'settled'
  | 'leaving'
  | 'crossfade-in'
  | 'crossfade-out'

export interface OverlayAnchor {
  left: number
  top: number
  fontSize: number
}

export interface SharedElementOverlayData {
  /** What the flying clone renders. Defaults to the source element's text (the leagues case, a bare
   * league name); callers with a richer anchor — the gameweek picker's name + points block — pass
   * the *destination's* markup so the final frame is a pixel match. */
  content: ReactNode
  /** Extra class on the clone, carrying the typography of whatever it is standing in for. The base
   *  class only positions it. */
  className?: string
  anchor: OverlayAnchor
}

interface Flight {
  geometry: FlightGeometry
  progress: number
  direction: 1 | -1
  durationMs: number
  easing: (progress: number) => number
  onDone: () => void
  raf: number
  lastTs: number
}

interface PendingExpand {
  sourceRect: TransitionRect
  sourceFontSize: number
  content: ReactNode
  className?: string
}

interface FlightStartup {
  geometry: FlightGeometry
  direction: 1 | -1
  durationMs: number
  easing: (progress: number) => number
  initialProgress: number
  onDone: () => void
}

export interface UseSharedElementTransitionOptions {
  /** Mount the destination for `id` (App flips its `selectedLeagueId` / `viewingHistoryGameweek`). */
  onEnter: (id: string) => void
  /** Unmount the destination (App clears that same state). */
  onExit: () => void
}

/** Optional per-activation overrides for what flies. Omitted → the clone is the source element's
 *  own text, styled by the base clone class alone. */
export interface ActivateOptions {
  content?: ReactNode
  className?: string
}

export interface SharedElementTransitionController {
  /** Destination's current entry state — drive its hidden/reveal classes off this. */
  detailEntry: DetailEntryState
  /** Overlay clone data (null when nothing is flying). */
  overlay: SharedElementOverlayData | null
  /** Ref callback for the flying clone element — the controller writes its transform each frame. */
  cloneRef: (el: HTMLElement | null) => void
  /** Ref callback the destination attaches to its anchor node (league name, gameweek heading block),
   *  so it can be measured once it mounts. */
  registerDestAnchor: (el: HTMLElement | null) => void
  /** True while a flight or cross-fade is in progress — source taps are blocked. */
  isTransitioning: boolean
  /** Open the destination for `id` from a tapped source. Fires the haptic, then either flies the
   *  shared element or cross-fades (reduced motion / degenerate rect). */
  activate: (
    id: string,
    sourceAnchorEl: HTMLElement | null,
    options?: ActivateOptions
  ) => void
  /** Fire the touch-down haptic + press feedback without committing (touch-down handler). */
  pressFeedback: () => void
  /** Begin the exit. Reverses an in-flight expand from its current progress; otherwise flies the
   *  clone back to the source (or cross-fades when there's no stored geometry / reduced motion). */
  beginBack: () => void
}

/** Owns a shared-element enter transition: the rAF flight controller, the phase machine, and the
 * overlay/clone state. Two instances exist: leagues, and the gameweek picker → results modal. The
 * pure geometry/easing lives in `sharedElementTransition.ts`.
 *
 * Exit note: there is no shared-element collapse — the *enter*-side
 * shared-element expand is kept (it has no gesture conflict), but the page now leaves via the
 * interactive swipe-back page-pop owned by `useSwipeBack`. `beginBack` therefore just exits: it
 * reverses an interrupted expand flight, cross-fades out under reduced motion, and is otherwise
 * instant (the visible slide is the swipe layer's). */
export function useSharedElementTransition(
  opts: UseSharedElementTransitionOptions
): SharedElementTransitionController {
  const { onEnter, onExit } = opts

  const [detailEntry, setDetailEntry] = useState<DetailEntryState>('idle')
  const [overlay, setOverlay] = useState<SharedElementOverlayData | null>(null)

  const cloneElRef = useRef<HTMLElement | null>(null)
  const destNameElRef = useRef<HTMLElement | null>(null)
  const flightRef = useRef<Flight | null>(null)
  const pendingExpandRef = useRef<PendingExpand | null>(null)
  const startupRef = useRef<FlightStartup | null>(null)
  const detailEntryRef = useRef<DetailEntryState>('idle')
  const crossfadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setEntry = useCallback((state: DetailEntryState) => {
    detailEntryRef.current = state
    setDetailEntry(state)
  }, [])

  const cloneRef = useCallback((el: HTMLElement | null) => {
    cloneElRef.current = el
  }, [])

  const applyFrame = useCallback((geometry: FlightGeometry, easedProgress: number) => {
    const el = cloneElRef.current
    if (!el) return
    el.style.transform = frameToTransform(flightFrameAt(geometry, easedProgress))
  }, [])

  const cancelFlight = useCallback(() => {
    if (flightRef.current) {
      cancelAnimationFrame(flightRef.current.raf)
      flightRef.current = null
    }
  }, [])

  const clearCrossfadeTimer = useCallback(() => {
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current)
      crossfadeTimerRef.current = null
    }
  }, [])

  // The rAF continuation is routed through a ref so `step` never references itself (which would tie
  // each frame to a stale closure). `step` reads only refs + the stable `applyFrame`, so it's safe
  // to hold in a ref across renders.
  const stepRef = useRef<(ts: number) => void>(() => {})

  const step = useCallback(
    (ts: number) => {
      const flight = flightRef.current
      if (!flight) return
      if (!flight.lastTs) flight.lastTs = ts
      const dt = ts - flight.lastTs
      flight.lastTs = ts

      flight.progress += flight.direction * (dt / flight.durationMs)
      let done = false
      if (flight.progress >= 1) {
        flight.progress = 1
        done = flight.direction === 1
      } else if (flight.progress <= 0) {
        flight.progress = 0
        done = flight.direction === -1
      }

      applyFrame(flight.geometry, flight.easing(flight.progress))

      if (done) {
        const onDone = flight.onDone
        flightRef.current = null
        onDone()
      } else {
        flight.raf = requestAnimationFrame(stepRef.current)
      }
    },
    [applyFrame]
  )

  useEffect(() => {
    stepRef.current = step
  }, [step])

  // Start a queued flight once its clone has actually committed to the DOM (ref set). Running this
  // in a layout effect — rather than a rAF right after setState — guarantees the clone element
  // exists and its initial frame is painted before the animation's first tick.
  useLayoutEffect(() => {
    const startup = startupRef.current
    if (!startup || !overlay) return
    startupRef.current = null
    // Paint the initial frame synchronously so the clone appears already matching source (expand)
    // or destination (collapse) — never a one-frame flash at identity.
    applyFrame(startup.geometry, startup.easing(startup.initialProgress))
    flightRef.current = {
      geometry: startup.geometry,
      progress: startup.initialProgress,
      direction: startup.direction,
      durationMs: startup.durationMs,
      easing: startup.easing,
      onDone: startup.onDone,
      raf: 0,
      lastTs: 0,
    }
    flightRef.current.raf = requestAnimationFrame(step)
  }, [overlay, applyFrame, step])

  const finishExit = useCallback(() => {
    cancelFlight()
    clearCrossfadeTimer()
    setOverlay(null)
    setEntry('idle')
    onExit()
  }, [cancelFlight, clearCrossfadeTimer, onExit, setEntry])

  const onExpandDone = useCallback(() => {
    // Reveal the real destination name and destroy the clone in the same commit — a seamless
    // handoff. Content staggers in off the `settled` state.
    setEntry('settled')
    setOverlay(null)
  }, [setEntry])

  const pressFeedback = useCallback(() => {
    fireLightHaptic()
  }, [])

  const crossfadeIn = useCallback(
    (id: string) => {
      clearCrossfadeTimer()
      setEntry('crossfade-in')
      onEnter(id)
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null
        setEntry('settled')
      }, CROSSFADE_MS)
    },
    [clearCrossfadeTimer, onEnter, setEntry]
  )

  const activate = useCallback(
    (id: string, sourceAnchorEl: HTMLElement | null, options?: ActivateOptions) => {
      // Block re-entrancy while a transition is running.
      if (detailEntryRef.current !== 'idle' && detailEntryRef.current !== 'settled') return
      if (flightRef.current) return

      // Haptic fires immediately on commit regardless of the path taken below.
      fireLightHaptic()

      const sourceRect = sourceAnchorEl ? rectOf(sourceAnchorEl) : null
      const reduced = prefersReducedMotion()

      if (reduced || !sourceAnchorEl || isDegenerateRect(sourceRect)) {
        crossfadeIn(id)
        return
      }

      pendingExpandRef.current = {
        sourceRect: sourceRect as TransitionRect,
        sourceFontSize: readFontSizePx(sourceAnchorEl),
        content: options?.content ?? sourceAnchorEl.textContent ?? '',
        className: options?.className,
      }
      setEntry('entering')
      onEnter(id)
    },
    [crossfadeIn, onEnter, setEntry]
  )

  const startExpandFlight = useCallback(() => {
    const pending = pendingExpandRef.current
    const destEl = destNameElRef.current
    if (!pending || !destEl) return
    pendingExpandRef.current = null

    const destRect = rectOf(destEl)
    const destFontSize = readFontSizePx(destEl)
    const geometry = computeFlightGeometry(
      pending.sourceRect,
      destRect,
      pending.sourceFontSize,
      destFontSize
    )

    if (!geometry) {
      // Degenerate destination — never animate from it. Reveal via a cross-fade instead.
      clearCrossfadeTimer()
      setEntry('crossfade-in')
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null
        setEntry('settled')
      }, CROSSFADE_MS)
      return
    }

    startupRef.current = {
      geometry,
      direction: 1,
      durationMs: EXPAND_FLIGHT_MS,
      easing: EXPAND_EASING,
      initialProgress: 0,
      onDone: onExpandDone,
    }
    setOverlay({
      content: pending.content,
      className: pending.className,
      anchor: { left: geometry.destLeft, top: geometry.destTop, fontSize: geometry.destFontSize },
    })
  }, [clearCrossfadeTimer, onExpandDone, setEntry])

  const registerDestAnchor = useCallback(
    (el: HTMLElement | null) => {
      destNameElRef.current = el
      if (el && pendingExpandRef.current && !flightRef.current && detailEntryRef.current === 'entering') {
        startExpandFlight()
      }
    },
    [startExpandFlight]
  )

  const beginBack = useCallback(() => {
    const entry = detailEntryRef.current
    const flight = flightRef.current

    // Interrupted expand: reverse the *same* controller from its current progress — never restart.
    if (entry === 'entering' && flight && flight.direction === 1) {
      flight.direction = -1
      flight.lastTs = 0
      flight.onDone = finishExit
      setEntry('leaving')
      return
    }

    // Reduced motion: cross-fade the whole route out — the no-motion exit.
    // (decision A retired the shared-element collapse; the
 // interactive page-pop slide is owned by the swipe-back layer now, so a
 // settled detail otherwise just exits instantly here.)
    if (prefersReducedMotion()) {
      clearCrossfadeTimer()
      setEntry('crossfade-out')
      crossfadeTimerRef.current = setTimeout(() => {
        crossfadeTimerRef.current = null
        finishExit()
      }, CROSSFADE_MS)
      return
    }

    finishExit()
  }, [clearCrossfadeTimer, finishExit, setEntry])

  const isTransitioning =
    detailEntry === 'entering' ||
    detailEntry === 'leaving' ||
    detailEntry === 'crossfade-in' ||
    detailEntry === 'crossfade-out'

  return {
    detailEntry,
    overlay,
    cloneRef,
    registerDestAnchor,
    isTransitioning,
    activate,
    pressFeedback,
    beginBack,
  }
}
